import {networkRetry} from '#/lib/async/retry'
import {isWeb} from '#/platform/detection'
import {
  DEFAULT_GEOLOCATION_CONFIG,
  GEOLOCATION_CONFIG_URL,
} from '#/state/geolocation/const'
import {emitGeolocationConfigUpdate} from '#/state/geolocation/events'
import {logger} from '#/state/geolocation/logger'
import {
  BAPP_CONFIG_DEV_BYPASS_SECRET,
  DISABLE_IP_CONFIG_FETCH,
  IS_DEV,
} from '#/env'
import {type Device, device} from '#/storage'

const IP_BSKY_CONFIG_TIMEOUT_MS = 3000
const IP_BSKY_CONFIG_CACHE_KEY = 'tells_ip_config_v1'
let didWarnLocalhostSkip = false
let didWarnDisableSkip = false

function isDevWebLocalhost() {
  if (!IS_DEV || !isWeb) {
    return false
  }
  if (typeof window === 'undefined') {
    return false
  }
  const hostname = window.location?.hostname
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

function shouldSkipIpBskyConfig() {
  if (DISABLE_IP_CONFIG_FETCH) {
    if (!didWarnDisableSkip) {
      console.warn(
        `geolocation config fetch disabled via EXPO_PUBLIC_DISABLE_IP_CONFIG_FETCH`,
      )
      didWarnDisableSkip = true
    }
    return true
  }
  if (!isDevWebLocalhost()) {
    return false
  }
  if (!didWarnLocalhostSkip) {
    console.warn(`geolocation config skipped on localhost (CORS)`)
    didWarnLocalhostSkip = true
  }
  return true
}

type IpBskyConfigResponse = {
  json: unknown | null
  error?: Error
}

type GeolocationConfigSource = 'remote' | 'cache' | 'fallback'

type GeolocationConfigResult = {
  config: Device['geolocation']
  source: GeolocationConfigSource
  shouldRetry: boolean
  skipped: boolean
}

type LocalStorageLike = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function getLocalStorageSafe(): LocalStorageLike | null {
  if (!isWeb || typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage ?? null
  } catch {
    return null
  }
}

function normalizeGeolocationConfig(
  json: unknown,
): Device['geolocation'] | null {
  if (!json || typeof json !== 'object') {
    return null
  }

  const data = json as {
    countryCode?: unknown
    regionCode?: unknown
    ageRestrictedGeos?: unknown
    ageBlockedGeos?: unknown
  }

  if (typeof data.countryCode !== 'string' || data.countryCode.length === 0) {
    return null
  }

  const ageRestrictedGeos = Array.isArray(data.ageRestrictedGeos)
    ? data.ageRestrictedGeos.filter(
        (value): value is string => typeof value === 'string',
      )
    : []
  const ageBlockedGeos = Array.isArray(data.ageBlockedGeos)
    ? data.ageBlockedGeos.filter(
        (value): value is string => typeof value === 'string',
      )
    : []

  return {
    countryCode: data.countryCode,
    regionCode:
      typeof data.regionCode === 'string' ? data.regionCode : undefined,
    ageRestrictedGeos,
    ageBlockedGeos,
  }
}

function readCachedGeolocationConfig(): Device['geolocation'] | null {
  const storage = getLocalStorageSafe()
  if (!storage) {
    return null
  }
  try {
    const raw = storage.getItem(IP_BSKY_CONFIG_CACHE_KEY)
    if (!raw) {
      return null
    }
    return normalizeGeolocationConfig(JSON.parse(raw))
  } catch {
    return null
  }
}

function writeCachedGeolocationConfig(config: Device['geolocation']) {
  const storage = getLocalStorageSafe()
  if (!storage) {
    return
  }
  try {
    storage.setItem(IP_BSKY_CONFIG_CACHE_KEY, JSON.stringify(config))
  } catch {
    // ignore storage errors
  }
}

async function loadIpBskyConfig(url: string): Promise<IpBskyConfigResponse> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    controller.abort()
  }, IP_BSKY_CONFIG_TIMEOUT_MS)

  try {
    const res = await fetch(url, {
      headers: IS_DEV
        ? {
            'x-dev-bypass-secret': BAPP_CONFIG_DEV_BYPASS_SECRET,
          }
        : undefined,
      signal: controller.signal,
    })

    if (!res.ok) {
      return {
        json: null,
        error: new Error(`config: fetch failed ${res.status}`),
      }
    }

    const json = await res.json()
    return {json}
  } catch (e: any) {
    const error = e instanceof Error ? e : new Error(String(e))
    return {json: null, error}
  } finally {
    clearTimeout(timeoutId)
  }
}

async function getGeolocationConfig(
  url: string,
): Promise<GeolocationConfigResult> {
  const cached = readCachedGeolocationConfig()
  if (shouldSkipIpBskyConfig()) {
    if (cached) {
      logger.warn(`config: using cached config (fetch skipped)`)
      return {
        config: cached,
        source: 'cache',
        shouldRetry: false,
        skipped: true,
      }
    }
    logger.warn(`config: using fallback config (fetch skipped)`)
    return {
      config: DEFAULT_GEOLOCATION_CONFIG,
      source: 'fallback',
      shouldRetry: false,
      skipped: true,
    }
  }

  const {json, error} = await loadIpBskyConfig(url)
  const config = normalizeGeolocationConfig(json)
  if (config) {
    writeCachedGeolocationConfig(config)
    logger.debug(`config: success`)
    return {config, source: 'remote', shouldRetry: false, skipped: false}
  }

  if (error) {
    logger.warn(`config: fetch failed`, {
      safeMessage: error.message,
    })
  } else {
    logger.warn(`config: invalid response`)
  }

  if (cached) {
    logger.warn(`config: using cached config after failure`)
    return {
      config: cached,
      source: 'cache',
      shouldRetry: Boolean(error),
      skipped: false,
    }
  }

  logger.warn(`config: using fallback config after failure`)
  return {
    config: DEFAULT_GEOLOCATION_CONFIG,
    source: 'fallback',
    shouldRetry: Boolean(error),
    skipped: false,
  }
}

async function fetchGeolocationConfigRemote(
  url: string,
): Promise<Device['geolocation']> {
  const {json, error} = await loadIpBskyConfig(url)
  if (error) {
    throw error
  }
  const config = normalizeGeolocationConfig(json)
  if (!config) {
    throw new Error(`config: invalid response`)
  }
  writeCachedGeolocationConfig(config)
  return config
}

/**
 * Local promise used within this file only.
 */
let geolocationConfigResolution: Promise<{success: boolean}> | undefined

/**
 * Begin the process of resolving geolocation config. This should be called
 * once at app start.
 *
 * THIS METHOD SHOULD NEVER THROW.
 *
 * This method is otherwise not used for any purpose. To ensure geolocation
 * config is resolved, use {@link ensureGeolocationConfigIsResolved}
 */
export function beginResolveGeolocationConfig() {
  /**
   * Here for debug purposes. Uncomment to prevent hitting the remote geo service, and apply whatever data you require for testing.
   */
  // if (__DEV__) {
  //   geolocationConfigResolution = new Promise(y => y({success: true}))
  //   device.set(['deviceGeolocation'], undefined) // clears GPS data
  //   device.set(['geolocation'], DEFAULT_GEOLOCATION_CONFIG) // clears bapp-config data
  //   return
  // }

  geolocationConfigResolution = new Promise(async resolve => {
    let success = true

    try {
      // Try once, fail fast
      const result = await getGeolocationConfig(GEOLOCATION_CONFIG_URL)
      device.set(['geolocation'], result.config)
      emitGeolocationConfigUpdate(result.config)
      success = result.source !== 'fallback' || result.skipped

      if (result.shouldRetry) {
        // retry 3 times, but don't await, proceed with fallback/cache
        networkRetry(3, () =>
          fetchGeolocationConfigRemote(GEOLOCATION_CONFIG_URL),
        )
          .then(config => {
            device.set(['geolocation'], config)
            emitGeolocationConfigUpdate(config)
            success = true
          })
          .catch((e: any) => {
            // complete fail closed
            logger.debug(`config: failed retries`, {
              safeMessage: e.message,
            })
          })
      }
    } catch (e: any) {
      success = false

      logger.debug(`config: failed initial request`, {
        safeMessage: e.message,
      })

      // set to default
      device.set(['geolocation'], DEFAULT_GEOLOCATION_CONFIG)
      emitGeolocationConfigUpdate(DEFAULT_GEOLOCATION_CONFIG)
    } finally {
      resolve({success})
    }
  })
}

/**
 * Ensure that geolocation config has been resolved, or at the very least attempted
 * once. Subsequent retries will not be captured by this `await`. Those will be
 * reported via {@link emitGeolocationConfigUpdate}.
 */
export async function ensureGeolocationConfigIsResolved() {
  if (!geolocationConfigResolution) {
    throw new Error(`config: beginResolveGeolocationConfig not called yet`)
  }

  const cached = device.get(['geolocation'])
  if (cached) {
    logger.debug(`config: using cache`)
  } else {
    logger.debug(`config: no cache`)
    const {success} = await geolocationConfigResolution
    if (success) {
      logger.debug(`config: resolved`)
    } else {
      logger.info(`config: failed to resolve`)
    }
  }
}
